import {cloneDeep, flatMap} from 'lodash';

import {areArraysShallowEqual} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {computeIsGlobalNamespace} from '../../../../helpers/models/namespaceHelpers';
import {DataFieldsTableModel} from '../../../../models/api/dataFieldsTableModel';
import {RuleBundleModelStrict, RuleBundlePrioritiesEnum} from '../../../../models/api/ruleBundleModel';
import {RuleBundleTypesEnum} from '../../../../models/api/ruleCommonTypes';
import {
  PartialRuleBaseModelStrict,
  PartialRuleModelStrict,
  RuleBaseModelStrict,
  RuleCompoundConditions,
  RuleCompoundConditionTypesEnum,
  RuleModelStrict,
} from '../../../../models/api/ruleModel';
import {decorateWithClientId} from '../../../editors/commons/clientIdModel';
import {CustomFields} from '../../../editors/commons/customFieldsModel';
import {EditorErrorsEnum} from '../../../editors/commons/editorErrors';
import {parseCompoundConditions} from '../../../editors/conditions/compoundConditionModel';
import {Expression, PreExpression, serializeExpression} from '../../../editors/expressions/expressionModel';
import {ExpressionState} from '../../../editors/expressions/expressionState';
import {ActivityResult} from '../../../editors/expressions/infer/expressionResultModel';
import {RuleRecipe} from '../../library/recipes';
import {resolvePreExpressions, RuleRecipePlaceholder} from '../../library/recipes/builder';
import {checkClientRuleActionsEquality} from '../../ruleActionHelpers';
import {ClientRuleAction, RuleAction, RuleActionTypesEnum} from '../../ruleActionModel';
import {checkClientRuleConditionsEquality, convertORConditions} from '../../ruleConditionHelpers';
import {ANDConditions, ClientRuleCondition, RuleConditionTypesEnum} from '../../ruleConditionModel';
import {RuleIntegration} from '../../ruleIntegrationModel';
import {
  buildTriggersWithConditions,
  checkTriggersWithConditionsEquality,
  eventsFromTriggers,
  extractTriggerConditionsFromState,
  triggersFromEvents,
  TriggerWithConditions,
} from '../../ruleTriggerHelpers';
import {getRuleEditorErrorsForChecks, RuleEditorErrorsEnum} from '../ruleEditorErrors';

/*
 * Constants.
 */

const getDefaultRulePreset = (namespace: string): RuleBaseModelStrict => ({
  id: undefined,
  name: '',
  description: '',
  active: true,
  stopProcessing: false,
  events: [],
  actions: [],
  compoundConditions: [
    RuleCompoundConditionTypesEnum.AND,
    {
      type: RuleConditionTypesEnum.INBOX_IN,
      params: {inboxes: null},
      restricted: ['tea', 'tim'],
      integration_id: 0,
    },
  ],
  expressions: [],
  sortOrder: 1,
  role: undefined,
  lastTriggeredAt: undefined,
  createdAt: undefined,
  namespace,
  footprintNamespaces: [],
});

/*
 * Types.
 */

export interface RuleState {
  name: string;
  sortOrder?: number;
  description: string;
  triggers: ReadonlyArray<TriggerWithConditions>;
  conditions: ReadonlyArray<ReadonlyArray<ClientRuleCondition>>;
  actions: ReadonlyArray<ClientRuleAction>;
  isActive: boolean;
  shouldStopProcessing: boolean;
  footprintNamespaces: ReadonlyArray<string>;
  exampleResourceId?: string;
}

/** Index is the clientId of the atom */
export type EditorStateErrorMap = {[index: string]: EditorErrorsEnum};

export interface RuleEditorState extends ExpressionState {
  ruleState: RuleState;
  initialRule?: PartialRuleModelStrict;
  errors: EditorStateErrorMap;
  notDisplayingErrorCards: ReadonlyArray<string>; // clientId or trigger enum of cards that should not display errors.

  dataTable?: DataFieldsTableModel;
}

/*
 * State.
 */

export const RuleEditorState = {
  get default(): RuleEditorState {
    return {
      ...ExpressionState.makeDefault(ActivityResult),
      ruleState: {
        name: '',
        description: '',
        triggers: [],
        conditions: [],
        actions: [],

        footprintNamespaces: [],
        isActive: true,
        shouldStopProcessing: false,
        sortOrder: 1,
      },
      errors: {},
      notDisplayingErrorCards: [],
    };
  },

  createInitialState(
    initialRule: PartialRuleModelStrict | undefined,
    customFields: CustomFields,
    initialDataTable: DataFieldsTableModel | undefined,
    namespace?: string,
    recipe?: RuleRecipe,
    hiddenErrors: ReadonlyArray<string> = [],
  ) {
    // For a rule being edited, we don't hide errors - they should not be here in the first place.
    if (initialRule) {
      return {
        ...RuleEditorState.fromRule(initialRule, customFields, initialDataTable),
        initialRule,
        notDisplayingErrorCards: hiddenErrors,
        dataTable: cloneDeep(initialDataTable),
      };
    }

    // ... but if the rule is created from a default, we hide the initial errors.
    const state = RuleEditorState.fromDefault(customFields, namespace, recipe);
    return {
      ...state,
      notDisplayingErrorCards: [...getInitialClientIds(state.ruleState), ...hiddenErrors],
      dataTable: cloneDeep(initialDataTable),
    };
  },

  fromDefault(customFields: CustomFields, namespace?: string, recipe?: RuleRecipe): RuleEditorState {
    if (recipe && namespace) {
      return this.fromRule(
        {
          ...getDefaultRulePreset(namespace),
          events: recipe.rule.events,
          compoundConditions: recipe.rule.conditions,
          actions: recipe.rule.actions,
          expressions: processRecipePreExpressions(
            customFields,
            recipe.rule.expressions,
            recipe.placeholders,
          ),
        },
        customFields,
        undefined,
      );
    }

    // Company rules are not compatible with the inbox_in filter used in the default preset.
    if (namespace && !computeIsGlobalNamespace(namespace)) {
      return this.fromRule(getDefaultRulePreset(namespace), customFields, undefined);
    }

    return {
      ...this.default,
      customFields,
    };
  },

  fromRule<T extends PartialRuleBaseModelStrict>(
    rule: T,
    customFields: CustomFields,
    dataTable: DataFieldsTableModel | undefined,
  ): RuleEditorState {
    const {triggersWithConditions, remainingConditions} = buildTriggersWithConditions(
      triggersFromEvents(rule.events ?? []),
      parseCompoundConditions(convertORConditions(rule).compoundConditions),
    );

    return {
      ...this.default,
      ...ExpressionState.createInitialState(rule.expressions || [], customFields, ActivityResult, {
        dataTable,
      }),
      ruleState: {
        name: rule.name ?? '',
        description: rule.description ?? '',
        sortOrder: rule.sortOrder,
        triggers: triggersWithConditions,
        conditions: remainingConditions,
        actions: reorderRuleActions(rule.actions ?? []).map(decorateWithClientId),
        isActive: Boolean(rule.active),
        shouldStopProcessing: rule.stopProcessing ?? false,
        footprintNamespaces: rule.footprintNamespaces ?? [],
      },
    };
  },

  toBundle(
    state: RuleEditorState,
    namespace: string,
    shouldWrapTriggerConditions?: boolean,
  ): RuleBundleModelStrict {
    const rule = RuleEditorState.toRule(state, namespace, shouldWrapTriggerConditions);
    const bundle = state.initialRule?.bundle;

    return {
      id: bundle?.id,
      name: state.ruleState.name,
      type: RuleBundleTypesEnum.SINGLE,
      priority: bundle?.priority ?? RuleBundlePrioritiesEnum.DEFAULT,
      namespace: bundle?.namespace ?? rule.namespace ?? namespace,
      sortOrder: bundle?.sortOrder,
      url: undefined,
      rules: [rule],
      dataFieldsTableId: bundle?.dataFieldsTableId ?? null,
    };
  },

  toRuleWithBundle(
    state: RuleEditorState,
    namespace: string,
    shouldWrapTriggerConditions?: boolean,
  ): RuleModelStrict {
    const {rules, ...bundle} = RuleEditorState.toBundle(state, namespace, shouldWrapTriggerConditions);
    return {
      ...rules[0],
      bundle,
    };
  },

  toRule(
    state: RuleEditorState,
    namespace: string,
    shouldWrapTriggerConditions?: boolean,
  ): RuleBaseModelStrict {
    const {ruleState} = state;

    const regularConditions = ruleState.conditions.map((orConds) => [
      RuleCompoundConditionTypesEnum.OR,
      ...orConds,
    ]);

    const conditions = [
      RuleCompoundConditionTypesEnum.AND,
      // The backend is not aware of conditions bound to triggers. Merge the triggers conditions with the regular ones.
      ...extractTriggerConditionsFromState(ruleState, shouldWrapTriggerConditions),
      ...regularConditions,
    ] as RuleCompoundConditions;

    return {
      id: state.initialRule?.id,
      name: ruleState.name,
      description: ruleState.description,
      compoundConditions: conditions,
      actions: reorderRuleActions(ruleState.actions),
      active: ruleState.isActive,
      events: eventsFromTriggers(ruleState.triggers.map(({trigger}) => trigger)),
      expressions: state.expressions.map(serializeExpression),
      stopProcessing: ruleState.shouldStopProcessing,
      footprintNamespaces: ruleState.footprintNamespaces,
      namespace,
      // Assuming we are generating single rule bundles, the order will always be 1 (the 1st in the bundle).
      sortOrder: ruleState.sortOrder || 1,
      role: undefined,
      lastTriggeredAt: undefined,
    };
  },

  hasDiff(a: RuleEditorState, b: RuleEditorState) {
    if (a === b) {
      return false;
    }

    const aState = a.ruleState;
    const bState = b.ruleState;

    if (aState.name !== bState.name) {
      return true;
    }

    if (aState.shouldStopProcessing !== bState.shouldStopProcessing || aState.isActive !== bState.isActive) {
      return true;
    }

    if (!areArraysShallowEqual(aState.triggers, bState.triggers, checkTriggersWithConditionsEquality)) {
      return true;
    }

    if (!areArraysShallowEqual(a.expressions, b.expressions, Expression.equals)) {
      return true;
    }

    if (
      !areArraysShallowEqual(aState.conditions, bState.conditions, (condsA, condsB) =>
        areArraysShallowEqual(condsA, condsB, checkClientRuleConditionsEquality),
      )
    ) {
      return true;
    }

    return !areArraysShallowEqual(aState.actions, bState.actions, checkClientRuleActionsEquality);
  },

  getEditorErrors(
    state: RuleEditorState,
    ruleIntegration: RuleIntegration,
    hasHighlightErrorsFlag: boolean,
  ): ReadonlyArray<RuleEditorErrorsEnum> {
    return getRuleEditorErrorsForChecks(
      state,
      ruleIntegration,
      ['name', 'triggers', 'conditions', 'actions', 'expressions', 'errors', 'dataTable'],
      hasHighlightErrorsFlag,
    );
  },
};

/*
 * Helpers.
 */

// This method reorder the action to make sure MOVE and TRASH actions are last.
const reorderRuleActions = (actions: ReadonlyArray<RuleAction>): ReadonlyArray<RuleAction> => {
  const moveActions = actions.filter((a) => a.type === RuleActionTypesEnum.MOVE);
  const trashActions = actions.filter((a) => a.type === RuleActionTypesEnum.TRASH);
  const otherActions = actions.filter(
    (a) => a.type !== RuleActionTypesEnum.MOVE && a.type !== RuleActionTypesEnum.TRASH,
  );
  return [...otherActions, ...moveActions, ...trashActions];
};

const processRecipePreExpressions = (
  customFields: CustomFields,
  expressions?: ReadonlyArray<PreExpression>,
  placeholders?: ReadonlyArray<RuleRecipePlaceholder>,
) => {
  if (!expressions) {
    return undefined;
  }

  if (!placeholders) {
    return expressions;
  }

  return resolvePreExpressions(customFields, expressions, placeholders);
};

const getAndConditionsClientIds = (andConditions: ANDConditions): ReadonlyArray<string> =>
  flatMap(andConditions, (orConditions) => flatMap(orConditions, (condition) => condition.clientId));

const getInitialClientIds = (ruleState: RuleState): ReadonlyArray<string> => {
  const triggerNames = ruleState.triggers.map((triggerWithCondition) => triggerWithCondition.trigger);
  const conditionIds = getAndConditionsClientIds(ruleState.conditions);
  const actionIds = ruleState.actions.map((action) => action.clientId);

  return [...triggerNames, ...conditionIds, ...actionIds];
};
